import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Banknote, Loader2, PackageCheck, Undo2 } from "lucide-react";

import StoreModal from "./StoreModal";
import { useHrGetStaffQuery } from "../../hr/api/hrApi";
import { errMsg, fmtQty, staffName, todayIso } from "../utils/stores";
import {
  useStoreGetItemsQuery,
  useStoreGetStoresQuery,
  useStoreGetHoldingsQuery,
  useStoreTransferMutation,
  useStoreIssueToAdminMutation,
  useStoreRecordSaleMutation,
  useStoreReturnToStoreMutation,
} from "../api/storesApi";

const inputCls =
  "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-xs font-medium text-gray-600 mb-1";

export type StockAction = "transfer" | "issue" | "sale" | "return";

const TITLES: Record<StockAction, string> = {
  transfer: "Move stock to another store",
  issue: "Issue stock to a member of staff",
  sale: "Record a sale",
  return: "Return stock to a store",
};

const ICONS: Record<StockAction, React.ReactNode> = {
  transfer: <ArrowLeftRight className="w-4 h-4 text-indigo-600" />,
  issue: <PackageCheck className="w-4 h-4 text-blue-600" />,
  sale: <Banknote className="w-4 h-4 text-emerald-600" />,
  return: <Undo2 className="w-4 h-4 text-amber-600" />,
};

const BLURB: Record<StockAction, string> = {
  transfer: "The sending store loses it, the receiving store gains it. Both stock cards record the move.",
  issue: "The stock leaves the store into their hands. It stays on the books as held by them until it is sold or returned.",
  sale: "Debits what they are holding. The stock has already left the store, so no store balance changes.",
  return: "Their holding falls and the store's balance rises.",
};

/**
 * The moves that involve two places — another store, or a person.
 *
 * Kept apart from MovementModal (receipt / issue / adjustment), which moves one
 * store's balance and nothing else. These each have a FROM and a TO, so the
 * form's job is to make both ends explicit and to show what is actually
 * available at the FROM end before anything is submitted.
 */
export default function StockActionModal({
  action: initialAction,
  item,
  storeId: initialStoreId,
  onClose,
}: {
  action: StockAction;
  item?: any | null;
  /** Opened from a store's own card — that store is the one being moved from. */
  storeId?: string | number | null;
  onClose: () => void;
}) {
  const [action, setAction] = useState<StockAction>(initialAction);
  const [error, setError] = useState<string | null>(null);

  const { data: itemsData } = useStoreGetItemsQuery({ status: "active" });
  const { data: stores = [] } = useStoreGetStoresQuery({ active_only: true });
  const { data: staffData } = useHrGetStaffQuery({});
  const { data: holdings = [] } = useStoreGetHoldingsQuery(undefined, {
    skip: action !== "sale" && action !== "return",
  });

  const [transfer, { isLoading: transferring }] = useStoreTransferMutation();
  const [issue, { isLoading: issuing }] = useStoreIssueToAdminMutation();
  const [sale, { isLoading: selling }] = useStoreRecordSaleMutation();
  const [giveBack, { isLoading: returning }] = useStoreReturnToStoreMutation();

  const busy = transferring || issuing || selling || returning;

  const [form, setForm] = useState({
    store_item_id: item?.id ? String(item.id) : "",
    from_store_id: initialStoreId ? String(initialStoreId) : "",
    to_store_id: "",
    store_id: initialStoreId ? String(initialStoreId) : "",
    admin_id: "",
    quantity: "",
    unit_price: "",
    sold_to: "",
    movement_date: todayIso(),
    reference: "",
    note: "",
  });

  const items = useMemo(() => itemsData?.rows ?? [], [itemsData]);
  const staff = useMemo(() => (staffData as any)?.rows ?? (staffData as any)?.data ?? [], [staffData]);
  const selected = items.find((i: any) => String(i.id) === form.store_item_id) ?? item;

  // One store: choose it rather than asking a question with one answer.
  useEffect(() => {
    if (stores.length !== 1) return;
    setForm((f) => ({
      ...f,
      from_store_id: f.from_store_id || String(stores[0].id),
      store_id: f.store_id || String(stores[0].id),
    }));
  }, [stores]);

  /** What the source store holds of the chosen item — the ceiling on this move. */
  const availableInStore = useMemo(() => {
    const storeId = action === "transfer" ? form.from_store_id : form.store_id;
    if (!selected?.balances || !storeId) return null;
    const row = selected.balances.find((b: any) => String(b.store_id) === storeId);
    return row ? Number(row.quantity) : 0;
  }, [selected, form.from_store_id, form.store_id, action]);

  /**
   * The store this was opened from, if any.
   *
   * When there is one, it is not a question: you move the stock of the store
   * whose card you pressed. Offering a picker there invites the one mistake
   * the API will refuse anyway — trying to move somebody else's stock.
   */
  const lockedStore = useMemo(
    () => (initialStoreId ? stores.find((s: any) => String(s.id) === String(initialStoreId)) : null),
    [stores, initialStoreId],
  );

  const fromIsMain = useMemo(
    () => stores.some((s: any) => String(s.id) === form.from_store_id && s.is_main),
    [stores, form.from_store_id],
  );

  /**
   * Where this store may send stock.
   *
   * Hub and spokes: the main store reaches every store; every other store
   * reaches only the main store. Offering the impossible ones and refusing
   * them afterwards teaches the rule the hard way.
   */
  const destinations = useMemo(() => {
    const others = stores.filter((s: any) => String(s.id) !== form.from_store_id);
    return fromIsMain ? others : others.filter((s: any) => s.is_main);
  }, [stores, form.from_store_id, fromIsMain]);

  // A branch has exactly one place to send stock — the main store. The field
  // is shown as a statement rather than a picker, so the value is set here.
  useEffect(() => {
    if (action !== "transfer" || fromIsMain || destinations.length !== 1) return;
    setForm((f) => (f.to_store_id ? f : { ...f, to_store_id: String(destinations[0].id) }));
  }, [action, fromIsMain, destinations]);

  /** What the chosen person is holding of the chosen item. */
  const heldByAdmin = useMemo(() => {
    if (!form.admin_id || !form.store_item_id) return null;
    const row = (holdings as any[]).find(
      (h) => String(h.admin_id) === form.admin_id && String(h.store_item_id) === form.store_item_id,
    );
    return row ? Number(row.quantity) : 0;
  }, [holdings, form.admin_id, form.store_item_id]);

  /** Only the people actually holding something, when that is what we are debiting. */
  const holders = useMemo(() => {
    if (action !== "sale" && action !== "return") return staff;
    const ids = new Set((holdings as any[]).map((h) => String(h.admin_id)));
    return staff.filter((a: any) => ids.has(String(a.id)));
  }, [staff, holdings, action]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const qty = Number(form.quantity);
    const shared = {
      store_item_id: form.store_item_id,
      quantity: qty,
      movement_date: form.movement_date,
      reference: form.reference.trim() || undefined,
      note: form.note.trim() || undefined,
    };

    try {
      if (action === "transfer") {
        await transfer({ ...shared, from_store_id: form.from_store_id, to_store_id: form.to_store_id }).unwrap();
      } else if (action === "issue") {
        await issue({ ...shared, store_id: form.store_id, admin_id: form.admin_id }).unwrap();
      } else if (action === "sale") {
        await sale({
          ...shared,
          admin_id: form.admin_id,
          unit_price: form.unit_price !== "" ? Number(form.unit_price) : undefined,
          sold_to: form.sold_to.trim() || undefined,
        }).unwrap();
      } else {
        await giveBack({ ...shared, admin_id: form.admin_id, store_id: form.store_id }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const unit = selected?.unit ?? "";

  return (
    <StoreModal title={TITLES[action]} icon={ICONS[action]} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>What is happening *</label>
          <div className="grid grid-cols-2 gap-2">
            {(["transfer", "issue", "sale", "return"] as StockAction[]).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAction(a)}
                className={`px-3 py-2 text-xs border rounded-lg transition-colors ${
                  action === a ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {a === "transfer" ? "Store → store"
                  : a === "issue" ? "Store → staff"
                  : a === "sale" ? "Sale by staff"
                  : "Staff → store"}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{BLURB[action]}</p>
        </div>

        <div>
          <label className={labelCls}>Item *</label>
          <select
            required
            value={form.store_item_id}
            onChange={(e) => setForm((f) => ({ ...f, store_item_id: e.target.value }))}
            className={inputCls}
          >
            <option value="">Select item…</option>
            {items.map((i: any) => (
              <option key={i.id} value={i.id}>
                {i.name} ({i.code})
              </option>
            ))}
          </select>
        </div>

        {action === "transfer" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>From store *</label>
              {/* Opened from a store's own card, so the store is a fact, not a
                  question — you move YOUR stock, never somebody else's. */}
              {lockedStore ? (
                <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm font-medium text-gray-800">
                  {lockedStore.name}
                </div>
              ) : (
                <select
                  required
                  value={form.from_store_id}
                  onChange={(e) => setForm((f) => ({ ...f, from_store_id: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select…</option>
                  {stores.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className={labelCls}>To store *</label>
              {/* From a branch there is exactly one legal destination — the
                  main store — so it is stated rather than offered. */}
              {destinations.length === 1 && !fromIsMain ? (
                <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm font-medium text-gray-800">
                  {destinations[0].name}
                </div>
              ) : (
                <select
                  required
                  value={form.to_store_id}
                  onChange={(e) => setForm((f) => ({ ...f, to_store_id: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">Select…</option>
                  {destinations.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {fromIsMain
                  ? "The main store supplies the others."
                  : "Stock goes back to the main store — branch stores do not exchange directly."}
              </p>
            </div>
          </div>
        ) : action === "sale" ? null : (
          <div>
            <label className={labelCls}>{action === "issue" ? "From store *" : "Back into store *"}</label>
            {lockedStore ? (
              <div className="px-3 py-2 rounded-lg bg-gray-50 border text-sm font-medium text-gray-800">
                {lockedStore.name}
              </div>
            ) : (
              <select
                required
                value={form.store_id}
                onChange={(e) => setForm((f) => ({ ...f, store_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">Select…</option>
                {stores.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {action === "issue" && (
              <p className="text-xs text-gray-500 mt-1">
                Stock is issued out of this store into their hands.
              </p>
            )}
          </div>
        )}

        {action !== "transfer" && (
          <div>
            <label className={labelCls}>
              {action === "issue" ? "Issue to *" : action === "sale" ? "Sold by *" : "Returned by *"}
            </label>
            <select
              required
              value={form.admin_id}
              onChange={(e) => setForm((f) => ({ ...f, admin_id: e.target.value }))}
              className={inputCls}
            >
              <option value="">Select staff…</option>
              {holders.map((a: any) => <option key={a.id} value={a.id}>{staffName(a)}</option>)}
            </select>
            {(action === "sale" || action === "return") && holders.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                Nobody is holding any stock yet — issue some to a member of staff first.
              </p>
            )}
            {heldByAdmin !== null && selected && action !== "issue" && (
              <p className="text-xs text-gray-500 mt-1">
                They are holding {fmtQty(heldByAdmin)} {unit} of {selected.name}.
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Quantity{unit ? ` (${unit})` : ""} *</label>
            <input
              type="number"
              required
              step="0.01"
              min="0.01"
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              className={inputCls}
            />
            {availableInStore !== null && action !== "sale" && action !== "return" && selected && (
              <p className="text-xs text-gray-500 mt-1">
                That store holds {fmtQty(availableInStore)} {unit}.
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Date *</label>
            <input
              type="date"
              required
              value={form.movement_date}
              onChange={(e) => setForm((f) => ({ ...f, movement_date: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {action === "sale" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Unit price</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.unit_price}
                onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))}
                className={inputCls}
              />
              {form.unit_price !== "" && form.quantity !== "" && (
                <p className="text-xs text-gray-500 mt-1">
                  Total ₦{(Number(form.unit_price) * Number(form.quantity)).toLocaleString()}
                </p>
              )}
            </div>
            <div>
              <label className={labelCls}>Sold to</label>
              <input
                value={form.sold_to}
                onChange={(e) => setForm((f) => ({ ...f, sold_to: e.target.value }))}
                className={inputCls}
                placeholder="Name or organisation"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Reference</label>
            <input
              value={form.reference}
              onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
              className={inputCls}
              placeholder="Waybill / receipt no."
            />
          </div>
          <div>
            <label className={labelCls}>Note</label>
            <input
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              className={inputCls}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-sm font-medium rounded-lg disabled:opacity-40"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Record
          </button>
        </div>
      </form>
    </StoreModal>
  );
}
